import React from "react";
import { compose } from "redux";
import { connect } from "react-redux";
import { createStructuredSelector } from "reselect";
import { useTranslation } from "react-i18next";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Dialog from "../../../components/Dialog";
import SlideUpTransition from "../../../components/SlideUpTransition";
import { Flex } from "../../../components/Container/Flex";
import Button from "../../../components/Controls/Button";
import Select from "../../../components/Controls/Select";
import TextButton from "../../../components/Controls/TextButton";
import TextField from "../../../components/Controls/TextField";
import { Colors } from "../../../utils/style/colors";
import { CheckIcon } from "../../../utils/icons";
import { setEditAppPage } from "./actions";
import { selectEditAppPage } from "./selectors";
import PhoneMockup from "./components/PhoneMockup";
import PhonePageGrid from "./components/PhonePageGrid";
import * as styles from "./style";

const COLUMN_OPTIONS = [
  { label: "2", value: 2 },
  { label: "3", value: 3 },
  { label: "4", value: 4 },
];

// The tile composer for one view: it needs more room than the DevCon panel has,
// so it opens over it. Tile edits stay in the buffer until Done calls `onSave`,
// which only adds them to the panel's pending changes.
const Composer = ({ editPage, onSave, setEditAppPage }) => {
  const { t } = useTranslation();
  const [name, setName] = React.useState("");
  const editId = editPage?.get("id");

  // Seed once per view: every tile edit makes a new record and would undo a rename.
  React.useEffect(() => {
    if (editPage) setName(editPage.get("name"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editId]);

  const close = () => setEditAppPage(null);

  return (
    <Dialog
      open={!!editPage}
      onError={{
        catch: (reset) => {
          close();
          reset();
        },
      }}
      onClose={close}
      TransitionComponent={SlideUpTransition}
      fullWidth
      maxWidth="md"
    >
      <DialogTitle>
        <Flex row align="center" style={{ gap: 16, flexWrap: "wrap" }}>
          <TextField
            label={t("name")}
            value={name}
            onChange={(value) => setName(value)}
          />
          <Select
            label={t("gridColumns", { defaultValue: "Columns" })}
            value={editPage?.get("columns") || 2}
            onChange={(columns) =>
              setEditAppPage(editPage.set("columns", columns))
            }
            items={COLUMN_OPTIONS}
          />
        </Flex>
      </DialogTitle>
      <DialogContent style={{ background: Colors.gray20 }}>
        {editPage && (
          <styles.ComposerArea>
            <PhoneMockup>
              <PhonePageGrid page={editPage} />
            </PhoneMockup>
          </styles.ComposerArea>
        )}
      </DialogContent>
      <DialogActions>
        <TextButton onClick={close}>{t("cancel")}</TextButton>
        <Button
          color={Colors.success}
          size="small"
          disabled={!name}
          startIcon={<CheckIcon />}
          onClick={() => onSave(editPage.set("name", name))}
        >
          {t("done", { defaultValue: "Done" })}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

function mapDispatchToProps(dispatch) {
  return {
    setEditAppPage: (page) => dispatch(setEditAppPage(page)),
  };
}

const mapStateToProps = createStructuredSelector({
  editPage: selectEditAppPage(),
});

export default compose(connect(mapStateToProps, mapDispatchToProps))(Composer);
